import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLink, getPageLinkByNodeId, LinkModel, mapLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { LinkListStyles } from "@tIntermatic/Overrides/Widgets/Basic/LinkList";
import * as React from "react";
import { useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    links = "links",
}

interface ResourceLinkModel {
    fields: {
        destination: LinkFieldValue;
        openInNewWindow: boolean;
        overriddenTitle: string;
        isDownloadable?: boolean;
        pdfLink: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.links]: ResourceLinkModel[];
    };
    extendedStyles?: LinkListStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    // const titleLink = getLink(state, ownProps.fields.titleLink);
    const links = mapLinks<
        ResourceLinkModel,
        { openInNewWindow: boolean; overriddenTitle: string; isDownloadable?: boolean; pdfLink: string }
    >(state, ownProps.fields.links, widgetLink => ({
        openInNewWindow: widgetLink.fields.openInNewWindow,
        overriddenTitle: widgetLink.fields.overriddenTitle,
        isDownloadable: widgetLink.fields.isDownloadable,
        pdfLink: widgetLink.fields.pdfLink,
    }));
    return {
        links,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ResourceDetailLinkListStyles {
    container?: GridContainerProps;
    subResourceNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    Rlptitle?: InjectableCss;
}

export const ResourceDetailLinkListStyles: ResourceDetailLinkListStyles = {
    container: {
        gap: 0,
        css: css`
            margin-top: 51px;
            padding-right: 32px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 33px;
                            padding-right: 0px;
                            padding-left: 0;
                            padding: 0 50px;
                            margin-bottom: 24px;
                        `,
                        css`
                            margin-top: 33px;
                            padding-right: 0px;
                            padding-left: 0;
                            padding: 0 50px;
                            margin-bottom: 24px;
                        `,
                        css`
                            margin-top: 33px;
                            padding-right: 0px;
                            padding-left: 0;
                            padding: 0 50px;
                            margin-bottom: 24px;
                        `,
                        css`
                            margin-top: 51px;
                            padding-right: 32px;
                            margin-bottom: 0px;
                            padding-left: 0;
                        `,
                        css`
                            margin-top: 51px;
                            padding-right: 32px;
                            padding-left: 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subResourceNameLinkItem: {
        width: 12,
        css: css`
            span {
                color: #333333 !important;
                font-size: 13px;
                letter-spacing: 1.08px;
                line-height: 30px;
                :hover {
                    color: #006bc0 !important;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 16px;
                                letter-spacing: 1.33px;
                                line-height: 40px;
                            `,
                            css`
                                font-size: 16px;
                                letter-spacing: 1.33px;
                                line-height: 40px;
                            `,
                            css`
                                font-size: 16px;
                                letter-spacing: 1.33px;
                                line-height: 40px;
                            `,
                            css`
                                color: #333333 !important;
                                font-size: 13px;
                                letter-spacing: 1.08px;
                                line-height: 30px;
                            `,
                            css`
                                color: #333333 !important;
                                font-size: 13px;
                                letter-spacing: 1.08px;
                                line-height: 30px;
                            `,
                        ],
                        "min",
                    )}
            }
        `,
    },
    Rlptitle: {
        css: css`
            font-size: 21px;
            font-weight: bold;
            letter-spacing: 1.75px;
            line-height: 25px;
            color: #33333a;
            margin-left: 7px;
            text-transform: uppercase;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 21px;
                            font-weight: 600;
                            letter-spacing: 0;
                            line-height: 25px;
                        `,
                        css`
                            font-size: 21px;
                            font-weight: 600;
                            letter-spacing: 0;
                            line-height: 25px;
                        `,
                        css`
                            font-size: 21px;
                            font-weight: bold;
                            letter-spacing: 1.75px;
                            line-height: 25px;
                            color: #33333a;
                            margin-left: 7px;
                        `,
                        css`
                            font-size: 14px;
                            font-weight: bold;
                            letter-spacing: 1.75px;
                            line-height: 25px;
                            color: #33333a;
                            margin-left: 7px;
                        `,
                        css`
                            font-size: 19px;
                            font-weight: bold;
                            letter-spacing: 1.75px;
                            line-height: 25px;
                            color: #33333a;
                            margin-left: 7px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

const styles = ResourceDetailLinkListStyles;

const ResourceDetailLinkList: React.FC<Props> = ({ fields, links, extendedStyles }) => {
    return (
        <GridContainer {...styles.container}>
            <StyledWrapper
                css={css`
                    border-bottom: 2px solid rgba(0, 0, 0, 0.1);
                    margin-bottom: 16px;
                    width: 100%;
                `}
            >
                <StyledWrapper
                    css={css`
                        display: flex;
                        margin-bottom: 19px;
                    `}
                >
                    <StyledWrapper
                        css={css`
                            display: flex;
                        `}
                    >
                        <LazyImage
                            src="/UserFiles/Images/MiniLogo.jpg"
                            css={css`
                                width: 20px;
                            `}
                        />
                    </StyledWrapper>
                    <StyledWrapper>
                        <Typography as="h3" {...styles.Rlptitle}>
                            {fields.title}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            {links.map((link, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <GridItem key={index} {...styles.subResourceNameLinkItem}>
                    {/* <SubCategoryLink subCategoryId={subCategoryId} /> */}
                    {(link?.url || link.pdfLink) && (
                        <Link
                            href={link.url || link.pdfLink}
                            {...styles.subCategoryNameLink}
                            target={link.openInNewWindow || link.isDownloadable ? "_blank" : ""}
                            download={!!link.isDownloadable}
                        >
                            {link.overriddenTitle || link.title}
                        </Link>
                    )}
                </GridItem>
            ))}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ResourceDetailLinkList),
    definition: {
        group: "Basic",
        icon: "LinkList",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        return value; // TODO ISC-10781 make this work
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "isDownloadable",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "pdfLink",
                        displayName: "Downloadable pdf",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: item => item.fields.isDownloadable,
                        isRequired: true,
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        isVisible: item => !item.fields.isDownloadable,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
